import { Collapsible } from '@base-ui/react/collapsible';
import { useRender } from '@base-ui/react/use-render';
import { ArrowUpRight, ChevronRight } from 'lucide-react';
import type { HTMLAttributes, ReactElement } from 'react';
import { Checkbox, type CheckboxProps } from '../primitives/checkbox.tsx';
import { cn } from '../util/classes.ts';
import { Container, type ContainerProps } from './container.tsx';

export type CardProps = ContainerProps & {
    open?: boolean;
    defaultOpen?: boolean;
    onOpenChange?: (open: boolean) => void;
};

export function Card({
    className,
    level,
    minLevel = 2,
    nestLevel = 1,
    open,
    defaultOpen,
    onOpenChange,
    render,
    children,
    ...props
}: CardProps): ReactElement {
    return (
        <Collapsible.Root
            open={open}
            defaultOpen={defaultOpen}
            onOpenChange={onOpenChange}
            render={
                <Container
                    render={render}
                    className={cn(
                        'flex flex-col items-justify relative',
                        'transition duration-state',
                        'l2:bg-container l2:rounded-default l2:border l2:border-line',
                        'has-data-[slot=card-main-action]:cursor-pointer',
                        'l2l3:has-data-[slot=card-main-action]:hover:bg-container-hover',
                        'card-expanded:bg-container-hover',
                        'card-interactive-child:relative card-interactive-child:z-1',
                        className,
                    )}
                    level={level}
                    minLevel={minLevel}
                    nestLevel={nestLevel}
                    data-slot="card"
                    {...props}
                />
            }
        >
            {children}
        </Collapsible.Root>
    );
}

export type CardActionsProps = HTMLAttributes<HTMLDivElement>;

export function CardActions({ className, children, ...props }: CardActionsProps): ReactElement {
    // we use another container here to drop the actions down a level in the hierarchy
    return (
        <Container
            data-slot="card-actions"
            className={cn(
                'card-expanded:border-line-weak card-expanded:not-card-completed:border-t',
                'card-completed:card-expanded:grid-rows-[0fr]',
                className,
            )}
            {...props}
            render={<CardExpandContent>{children}</CardExpandContent>}
        />
    );
}

export type CardCompleteProps = CheckboxProps;

export function CardComplete({ ...props }: CardCompleteProps): ReactElement {
    return <Checkbox data-slot="card-complete" {...props} />;
}

export type CardContentProps = useRender.ComponentProps<'div'>;

export function CardContent({ className, render, ...props }: CardContentProps): ReactElement {
    return useRender({
        defaultTagName: 'div',
        render,
        props: { ...props, className: cn('grow flex flex-col gap-container-cozy', className) },
    });
}

export type CardDetailProps = useRender.ComponentProps<'div'>;

export function CardDetail({ className, render, ...props }: CardDetailProps): ReactElement {
    return useRender({
        defaultTagName: 'div',
        render,
        props: { ...props, className: cn('text-content-weak', className) },
    });
}

export type CardEndProps = useRender.ComponentProps<'div'>;

export function CardEnd({ className, render, ...props }: CardEndProps): ReactElement {
    return useRender({
        defaultTagName: 'div',
        render,
        props: { ...props, className: cn('shrink-0 text-content-weak', className) },
    });
}

export type CardExpandActionProps = Collapsible.Trigger.Props;

export function CardExpandAction(props: CardExpandActionProps): ReactElement {
    return (
        <Collapsible.Trigger
            nativeButton={false}
            {...props}
            render={
                <CardMainAction className="flex items-center">
                    <ChevronRight className="transition-transform duration-state card-expanded:rotate-90" />
                </CardMainAction>
            }
        />
    );
}

export type CardExpandContentProps = Collapsible.Panel.Props & {
    gutter?: boolean;
};

export function CardExpandContent({
    className,
    children,
    gutter = true,
    ...props
}: CardExpandContentProps): ReactElement {
    return (
        <Collapsible.Panel
            keepMounted
            data-slot="card-panel"
            data-gutter={gutter}
            {...props}
            className={cn(
                'grid grid-rows-[0fr]',
                'card-expanded:grid-rows-[1fr]',
                'transition-[grid-template-rows] ease-out',
                'data-[gutter=true]:px-[calc(var(--spacing-container)+16px)]',
                className,
            )}
        >
            <div className="min-h-0 overflow-hidden">
                <div className="flex p-container gap-container">{children}</div>
            </div>
        </Collapsible.Panel>
    );
}

export type CardFrontProps = useRender.ComponentProps<'div'>;

export function CardFront({ className, render, ...props }: CardFrontProps): ReactElement {
    return useRender({
        defaultTagName: 'div',
        render,
        props: {
            ...props,
            className: cn(
                'flex l2:rounded-default',
                'p-container gap-container',
                'card-completed:line-through card-completed:text-content-weak',
                className,
            ),
            'data-slot': 'card-front',
        },
    });
}

export type CardHeaderProps = useRender.ComponentProps<'div'>;

export function CardHeader({ className, render, ...props }: CardHeaderProps): ReactElement {
    return useRender({
        defaultTagName: 'div',
        render,
        props: { ...props, className: cn('grow flex flex-wrap-reverse gap-container leading-4', className) },
    });
}

export type CardOpenIconProps = HTMLAttributes<HTMLElement | SVGSVGElement>;

export function CardOpenIcon(props: CardOpenIconProps): ReactElement {
    return <ArrowUpRight {...props} />;
}

export type CardMainActionProps = useRender.ComponentProps<'div'>;

export function CardMainAction({ className, render, ...props }: CardMainActionProps): ReactElement {
    return (
        <CardEnd
            className={cn(
                'cursor-pointer p-0 appearance-none flex',
                // this is a hack to make the whole card clickable without
                // having invalid dom nesting for cards with other
                // interactive components within them
                'after:absolute after:inset-0',
                className,
            )}
            data-slot="card-main-action"
            // the above hack doesn't work on actual buttons
            role={render ? undefined : 'button'}
            {...props}
            render={render}
        />
    );
}

export type CardStartProps = useRender.ComponentProps<'div'>;

export function CardStart({ className, render, ...props }: CardStartProps): ReactElement {
    return useRender({
        defaultTagName: 'div',
        render,
        props: { ...props, className: cn('shrink-0 text-content-weak', className) },
    });
}

export type CardSubtitleProps = useRender.ComponentProps<'div'>;

export function CardSubtitle({ className, render, ...props }: CardSubtitleProps): ReactElement {
    return useRender({
        defaultTagName: 'div',
        render,
        props: {
            ...props,
            className: cn('flex gap-tight text-ui-sm/4', 'text-content-weak', className),
        },
    });
}

export type CardTitleProps = useRender.ComponentProps<'div'>;

export function CardTitle({ className, render, ...props }: CardTitleProps): ReactElement {
    return useRender({
        defaultTagName: 'div',
        render,
        props: { ...props, className: cn('flex gap-tight grow', className) },
    });
}
